import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Brain, Sparkles, Send, TrendingUp, TrendingDown, 
  AlertTriangle, DollarSign, Lightbulb, RefreshCw,
  BarChart3, PieChart, Calendar, ArrowRight, Copy, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CostExplainer = () => {
  const { shouldShowData } = useDemoData();

  const [loading, setLoading] = useState(true);
  const [explaining, setExplaining] = useState(false);
  const [costData, setCostData] = useState(null);
  const [explanation, setExplanation] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('mtd');
  const [selectedFocus, setSelectedFocus] = useState('overview');
  const [customQuestion, setCustomQuestion] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchCostData();
  }, [selectedPeriod]);

  const fetchCostData = async () => {
    setLoading(true);
    try {
      const [summary, trends, services] = await Promise.all([
        axios.get(`${API_URL}/dashboard/summary`),
        axios.get(`${API_URL}/dashboard/trends`),
        axios.get(`${API_URL}/dashboard/by-service`)
      ]);
      setCostData({
        summary: summary.data,
        trends: trends.data,
        services: services.data
      });
    } catch (error) {
      toast.error('Failed to fetch cost data');
    } finally {
      setLoading(false);
    }
  };

  const explainCosts = async () => {
    if (!costData) return;
    
    setExplaining(true);
    setExplanation('');
    
    try {
      const response = await axios.post(`${API_URL}/ai/explain-costs`, {
        costData: costData,
        period: selectedPeriod,
        focus: selectedFocus,
        customQuestion: customQuestion || null
      });
      setExplanation(response.data.explanation);
    } catch (error) {
      // Generate a fallback explanation if AI fails
      generateFallbackExplanation();
    } finally {
      setExplaining(false);
    }
  };

  const generateFallbackExplanation = () => {
    if (!costData) return;
    
    const { summary, services } = costData;
    const topService = services?.[0];
    const changeDirection = summary.cost_change_percent >= 0 ? 'increased' : 'decreased';
    const changeAbs = Math.abs(summary.cost_change_percent).toFixed(1);
    
    let explanation = `## Cost Analysis Summary\n\n`;
    explanation += `Your cloud costs have **${changeDirection} by ${changeAbs}%** compared to last month.\n\n`;
    explanation += `### Key Highlights\n\n`;
    explanation += `- **Total MTD Spend:** $${summary.total_cost_mtd.toLocaleString()}\n`;
    explanation += `- **Active Anomalies:** ${summary.anomaly_count} detected\n`;
    explanation += `- **Potential Savings:** $${summary.potential_savings.toLocaleString()} identified\n\n`;
    
    if (topService) {
      explanation += `### Top Spending Service\n\n`;
      explanation += `**${topService.service}** is your highest cost service at $${topService.cost.toLocaleString()}, `;
      explanation += `representing ${((topService.cost / summary.total_cost_mtd) * 100).toFixed(0)}% of total spend.\n\n`;
    }
    
    explanation += `### Recommendations\n\n`;
    explanation += `1. Review the ${summary.anomaly_count} active anomalies for unexpected charges\n`;
    explanation += `2. Implement the identified $${summary.potential_savings.toLocaleString()} in savings opportunities\n`;
    explanation += `3. Consider setting up budget guardrails to prevent overspend\n`;
    
    setExplanation(explanation);
  };

  const copyExplanation = () => {
    navigator.clipboard.writeText(explanation);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const focusOptions = [
    { value: 'overview', label: 'General Overview', icon: BarChart3 },
    { value: 'anomalies', label: 'Anomaly Analysis', icon: AlertTriangle },
    { value: 'trends', label: 'Trend Insights', icon: TrendingUp },
    { value: 'optimization', label: 'Optimization Tips', icon: Lightbulb },
    { value: 'forecast', label: 'Future Forecast', icon: Calendar }
  ];

  const presetQuestions = [
    "Why did my costs increase this month?",
    "Which services should I optimize first?",
    "What's driving the anomalies?",
    "How can I reduce compute costs?",
    "Explain my cost trends like I'm a CFO"
  ];

  if (!shouldShowData) {
    return <EmptyDataState title="Cost Explainer" description="Connect your cloud account to see real data here." />;
  }

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="cost-explainer-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Card key={i}><CardContent className="p-6"><Skeleton className="h-24" /></CardContent></Card>
          ))}
        </div>
        <Card><CardContent className="p-6"><Skeleton className="h-64" /></CardContent></Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="cost-explainer-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-3">
            <Brain className="w-7 h-7 text-emerald-500" />
            AI Cost Explainer
          </h1>
          <p className="text-slate-400 dark:text-slate-400 text-sm mt-1">
            Get AI-powered insights and explanations for your cloud costs
          </p>
        </div>
        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-cyan-900/30 dark:text-cyan-400">
          <Sparkles className="w-3 h-3 mr-1" />
          Powered by Claude AI
        </Badge>
      </div>

      {/* Quick Stats */}
      {costData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="card-hover" data-testid="mtd-cost-stat">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">MTD Cost</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white">
                    {formatCurrency(costData.summary.total_cost_mtd)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="change-stat">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${costData.summary.cost_change_percent >= 0 ? 'bg-rose-100 dark:bg-rose-900/30' : 'bg-emerald-100 dark:bg-emerald-900/30'}`}>
                  {costData.summary.cost_change_percent >= 0 ? (
                    <TrendingUp className="w-5 h-5 text-rose-600" />
                  ) : (
                    <TrendingDown className="w-5 h-5 text-emerald-600" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-slate-400">vs Last Month</p>
                  <p className={`text-xl font-bold ${costData.summary.cost_change_percent >= 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                    {costData.summary.cost_change_percent >= 0 ? '+' : ''}{costData.summary.cost_change_percent.toFixed(1)}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="anomalies-stat">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Anomalies</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white">
                    {costData.summary.anomaly_count}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="savings-stat">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-100 dark:bg-cyan-900/30">
                  <Lightbulb className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Potential Savings</p>
                  <p className="text-xl font-bold text-emerald-600">
                    {formatCurrency(costData.summary.potential_savings)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Controls */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Ask AI
            </CardTitle>
            <CardDescription>
              Get intelligent explanations of your cost data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Time Period</label>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger data-testid="period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mtd">Month to Date</SelectItem>
                  <SelectItem value="last7">Last 7 Days</SelectItem>
                  <SelectItem value="last30">Last 30 Days</SelectItem>
                  <SelectItem value="lastMonth">Last Month</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Analysis Focus</label>
              <div className="grid grid-cols-1 gap-2">
                {focusOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSelectedFocus(option.value)}
                    className={`flex items-center gap-2 p-2 rounded-lg text-sm text-left transition-all ${
                      selectedFocus === option.value
                        ? 'bg-emerald-100 dark:bg-cyan-900/30 text-emerald-700 dark:text-cyan-400 border-2 border-emerald-500'
                        : 'bg-slate-50 dark:bg-slate-800 text-slate-400 dark:text-slate-400 border-2 border-transparent hover:border-slate-300'
                    }`}
                  >
                    <option.icon className="w-4 h-4" />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Custom Question (Optional)</label>
              <Textarea
                placeholder="Ask anything about your costs..."
                value={customQuestion}
                onChange={(e) => setCustomQuestion(e.target.value)}
                className="min-h-[80px]"
                data-testid="custom-question-input"
              />
            </div>

            <div className="space-y-2">
              <p className="text-xs text-slate-400">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {presetQuestions.map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCustomQuestion(q)}
                    className="text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-400 hover:bg-emerald-100 hover:text-emerald-700 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={explainCosts}
              disabled={explaining || !costData}
              className="w-full gap-2 bg-cyan-600 hover:bg-cyan-700"
              data-testid="explain-btn"
            >
              {explaining ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Brain className="w-4 h-4" />
                  Explain My Costs
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Explanation Output */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <PieChart className="w-5 h-5" />
                AI Analysis
              </CardTitle>
              {explanation && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyExplanation}
                  className="gap-2"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                  Copy
                </Button>
              )}
            </div>
            <CardDescription>
              Intelligent insights powered by AI
            </CardDescription>
          </CardHeader>
          <CardContent>
            {explaining ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="relative">
                  <Brain className="w-16 h-16 text-emerald-500 animate-pulse" />
                  <Sparkles className="w-6 h-6 text-amber-500 absolute -top-1 -right-1 animate-bounce" />
                </div>
                <p className="mt-4 text-slate-400">Analyzing your cost data...</p>
                <p className="text-sm text-slate-400">This may take a few seconds</p>
              </div>
            ) : explanation ? (
              <div className="prose prose-slate dark:prose-invert max-w-none">
                <div className="bg-emerald-50 bg-gradient-to-br from-emerald-50 to-slate-50 dark:from-emerald-900/20 dark:to-slate-900/20 rounded-xl p-6 border border-cyan-100 dark:border-cyan-900/30">
                  {explanation.split('\n').map((line, idx) => {
                    if (line.startsWith('## ')) {
                      return <h2 key={idx} className="text-xl font-bold text-slate-900 dark:text-white mt-4 first:mt-0">{line.replace('## ', '')}</h2>;
                    } else if (line.startsWith('### ')) {
                      return <h3 key={idx} className="text-lg font-semibold text-slate-800 dark:text-slate-200 mt-4">{line.replace('### ', '')}</h3>;
                    } else if (line.startsWith('- ')) {
                      return <li key={idx} className="text-slate-400 dark:text-slate-400 ml-4">{line.replace('- ', '').replace(/\*\*/g, '')}</li>;
                    } else if (line.match(/^\d\./)) {
                      return <li key={idx} className="text-slate-400 dark:text-slate-400 ml-4 list-decimal">{line.replace(/^\d\.\s/, '').replace(/\*\*/g, '')}</li>;
                    } else if (line.trim()) {
                      return <p key={idx} className="text-slate-400 dark:text-slate-400 my-2">{line.replace(/\*\*/g, '')}</p>;
                    }
                    return null;
                  })}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Brain className="w-16 h-16 text-slate-300 dark:text-slate-400" />
                <p className="mt-4 text-slate-400">Click "Explain My Costs" to get AI-powered insights</p>
                <p className="text-sm text-slate-400">Select a focus area and optionally add a question</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CostExplainer;
